/**
 * Public customer API (no auth). Plain `fetch` on purpose: the console's axios client carries
 * the Firebase token interceptor, and importing it here would pull firebase into this chunk.
 * Every draft request sends the answer to a one-time challenge (`solution`).
 */
import { API_BASE } from "@/lib/env";
import type { Challenge, Draft, DraftRequest, PageEvent, PublicPage } from "@/lib/api/types";
import { solve } from "./pow";

const base = `${API_BASE}/public`;

export interface Proof {
  challenge_id: string;
  solution: number;
  /** ms epoch. The server rejects answers after this. */
  expiresAt: number;
}

export class ReviewApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Seconds until a retry makes sense (429). */
    readonly retryAfter: number | null = null
  ) {
    super(message);
    this.name = "ReviewApiError";
  }
}

export const NETWORK_MESSAGE = "인터넷 연결을 확인하고 한 번 더 눌러 주세요.";
export const FALLBACK_MESSAGE = "지금은 초안을 만들지 못했어요. 잠시 뒤 다시 시도해 주세요.";

async function request<T>(
  method: "GET" | "POST",
  path: string,
  body?: unknown,
  init?: RequestInit
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      method,
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      cache: "no-store",
      ...init,
    });
  } catch {
    throw new ReviewApiError(0, "network", NETWORK_MESSAGE);
  }
  if (res.status === 204) return undefined as T;
  if (res.ok) return (await res.json()) as T;
  const detail = await res
    .json()
    .then((j: { detail?: { code?: string; message?: string; retry_after?: number } }) => j.detail)
    .catch(() => undefined);
  const retryHeader = Number(res.headers.get("Retry-After"));
  throw new ReviewApiError(
    res.status,
    detail?.code ??
      (res.status === 404 ? "not_found" : res.status >= 500 ? "unavailable" : "error"),
    detail?.message ?? FALLBACK_MESSAGE,
    typeof detail?.retry_after === "number"
      ? detail.retry_after
      : Number.isFinite(retryHeader) && retryHeader > 0
        ? retryHeader
        : null
  );
}

export function fetchPage(slug: string): Promise<PublicPage> {
  return request<PublicPage>("GET", `/pages/${encodeURIComponent(slug)}`);
}

/** Fire-and-forget counters. `keepalive` lets `platform_open` survive the navigation. */
export function sendEvent(slug: string, event: PageEvent): Promise<void> {
  return request<void>("POST", `/pages/${encodeURIComponent(slug)}/events`, event, {
    keepalive: true,
  }).catch(() => undefined);
}

/** Solve in a Web Worker; fall back to the main thread when workers are unavailable. */
export function solveInBackground(nonce: string, bits: number): Promise<number> {
  const inline = () => Promise.resolve().then(() => solve(nonce, bits));
  if (typeof Worker === "undefined") return inline();
  return new Promise((resolve, reject) => {
    let worker: Worker;
    try {
      worker = new Worker(new URL("./pow.worker.ts", import.meta.url), { type: "module" });
    } catch {
      inline().then(resolve, reject);
      return;
    }
    worker.onmessage = (e: MessageEvent<number>) => {
      worker.terminate();
      resolve(e.data);
    };
    worker.onerror = () => {
      worker.terminate();
      inline().then(resolve, reject);
    };
    worker.postMessage({ nonce, bits });
  });
}

export async function prepareProof(): Promise<Proof> {
  const ch = await request<Challenge>("POST", "/challenge");
  const solution = await solveInBackground(ch.nonce, ch.bits);
  return { challenge_id: ch.id, solution, expiresAt: Date.parse(ch.expires_at) };
}

export async function requestDrafts(
  slug: string,
  proof: Proof,
  req: DraftRequest
): Promise<Draft[]> {
  const res = await request<{ drafts: Draft[] }>(
    "POST",
    `/pages/${encodeURIComponent(slug)}/drafts`,
    { challenge_id: proof.challenge_id, solution: proof.solution, ...req }
  );
  return res.drafts.map((d) => ({ title: d.title ?? null, text: d.text }));
}

/** Answers with less than this left are dropped and re-solved (they may expire in flight). */
export const PROOF_MARGIN_MS = 30_000;

/**
 * Keeps one solved challenge warm. `take()` hands it out (or solves a fresh one) and the caller
 * calls `warm()` again after each generation.
 */
export function createProofPool(prepare: () => Promise<Proof> = prepareProof) {
  let pending: Promise<Proof> | null = null;
  return {
    warm() {
      const p = prepare();
      p.catch(() => {
        if (pending === p) pending = null;
      });
      pending = p;
    },
    async take(): Promise<Proof> {
      let proof = pending ? await pending.catch(() => null) : null;
      pending = null;
      if (!proof || proof.expiresAt - Date.now() < PROOF_MARGIN_MS) proof = await prepare();
      return proof;
    },
    fresh: prepare,
  };
}

export type ProofPool = ReturnType<typeof createProofPool>;
